/**
 * Canonical form label — wraps `<label>` with the project's chosen
 * sizing/color treatment. Used implicitly by <Input>, <Select>,
 * <Textarea>, <Checkbox>, <ToggleSwitch>, and can also be used
 * standalone above a custom control.
 *
 *   <Label htmlFor="email">Email</Label>
 *   <Label htmlFor="x" required>Phone</Label>
 *   <Label htmlFor="x" size="sm">Notes</Label>
 */

import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { getSecondaryText } from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";

interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /** Smaller font size for nested / dense forms. */
  size?: "sm" | "md";
  /** Show the red `*` required marker after the text. */
  required?: boolean;
}

const Label: React.FC<LabelProps> = ({
  size = "md",
  required = false,
  className = "",
  children,
  ...rest
}) => {
  const { isDarkMode } = useTheme();
  const base = size === "sm" ? TOKENS.label.sm : TOKENS.label.base;
  const classes = [base, getSecondaryText(isDarkMode), className]
    .filter(Boolean)
    .join(" ");

  // Many labels were written with their own "*" ("First Name *"). With the
  // required marker as well they read "First Name **" (2026-10-10). When the
  // marker is shown, a trailing "*" in the text is dropped.
  const text =
    required && typeof children === "string" ? children.replace(/\s*\*\s*$/, "") : children;

  return (
    <label className={classes} {...rest}>
      {text}
      {required && (
        <span className={TOKENS.label.required} aria-hidden="true">
          *
        </span>
      )}
    </label>
  );
};

export default Label;
